import {
	DEFAULT_LANGUAGE,
	SUPPORTED_LANGUAGES,
	localizedPath,
} from '#app/utils/i18n.ts'

const SITE_NAME = 'Open Sourced'
const OG_IMAGE_PATH = '/img/os/hq/logo-mit-text-horizontal.png'
const DEFAULT_SITE_URL = 'https://opensourced.ch'

type LayoutSeoData = {
	siteUrl: string
	lang: string
}

/**
 * Extract siteUrl and lang from parent route matches.
 * Falls back to defaults if not found (e.g. in tests).
 */
export function getLayoutSeoData(
	matches?: ReadonlyArray<{ data?: unknown } | undefined>,
): LayoutSeoData {
	if (matches) {
		for (const match of matches) {
			if (
				match?.data &&
				typeof match.data === 'object' &&
				'siteUrl' in match.data
			) {
				const data = match.data as Record<string, unknown>
				return {
					siteUrl: (data.siteUrl as string) || DEFAULT_SITE_URL,
					lang: (data.lang as string) || DEFAULT_LANGUAGE,
				}
			}
		}
	}
	return { siteUrl: DEFAULT_SITE_URL, lang: DEFAULT_LANGUAGE }
}

/**
 * Build the canonical URL for a marketing page.
 */
function canonicalUrl(siteUrl: string, lang: string, path: string): string {
	const base = siteUrl.replace(/\/$/, '')
	if (path === '/' || path === '') return `${base}/${lang}`
	return `${base}${localizedPath(lang, path)}`
}

type MarketingMetaArgs = {
	title: string
	description: string
	siteUrl: string
	lang: string
	path: string
	ogType?: string
}

/**
 * Generate complete meta tags (title, description, OG, Twitter Card, canonical)
 * for a marketing page.
 */
export function generateMarketingMeta({
	title,
	description,
	siteUrl,
	lang,
	path,
	ogType = 'website',
}: MarketingMetaArgs): Array<Record<string, unknown>> {
	const base = siteUrl.replace(/\/$/, '')
	const url = canonicalUrl(siteUrl, lang, path)
	const imageUrl = `${base}${OG_IMAGE_PATH}`

	return [
		{ title },
		{ name: 'description', content: description },
		// Canonical
		{ tagName: 'link', rel: 'canonical', href: url },
		// Open Graph
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: ogType },
		{ property: 'og:url', content: url },
		{ property: 'og:image', content: imageUrl },
		{ property: 'og:image:alt', content: SITE_NAME },
		{ property: 'og:locale', content: 'de_CH' },
		{ property: 'og:site_name', content: SITE_NAME },
		// Twitter Card
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:image', content: imageUrl },
		{ name: 'twitter:image:alt', content: SITE_NAME },
	]
}

// ── JSON-LD generators ─────────────────────────────────────

const ORGANIZATION_BASE = {
	'@type': 'Organization' as const,
	name: 'Open Sourced GmbH',
}

/**
 * Organization schema for the Home page.
 */
export function generateOrganizationJsonLd(siteUrl: string) {
	const base = siteUrl.replace(/\/$/, '')
	return {
		'@context': 'https://schema.org',
		...ORGANIZATION_BASE,
		url: base,
		logo: `${base}/favicons/android-chrome-512x512.png`,
		contactPoint: {
			'@type': 'ContactPoint',
			email: 'info@opensourced.ch',
			contactType: 'customer service',
			areaServed: 'CH',
			availableLanguage: ['German', 'French', 'English'],
		},
		address: {
			'@type': 'PostalAddress',
			addressLocality: 'Lostorf',
			addressCountry: 'CH',
		},
	}
}

type ServiceOffer = {
	'@type': 'Offer'
	price: string
	priceCurrency: string
	name?: string
	unitText?: string
}

/**
 * Shared scaffold for per-page Service schemas — keeps provider and
 * areaServed consistent across the service pages.
 */
function generateServiceBaseJsonLd({
	siteUrl,
	name,
	description,
}: {
	siteUrl: string
	name: string
	description: string
}) {
	const base = siteUrl.replace(/\/$/, '')
	return {
		'@context': 'https://schema.org',
		'@type': 'Service',
		name,
		description,
		provider: {
			...ORGANIZATION_BASE,
			url: base,
		},
		areaServed: { '@type': 'Country', name: 'CH' },
	}
}

function generateServiceJsonLd<T extends ServiceOffer | ServiceOffer[]>({
	offers,
	...service
}: Parameters<typeof generateServiceBaseJsonLd>[0] & { offers: T }) {
	return { ...generateServiceBaseJsonLd(service), offers }
}

/**
 * Service schema for the Websites page. No offers: every website gets an
 * individual quote, so the page publishes no price.
 */
export function generateWebsitesServiceJsonLd(siteUrl: string) {
	return generateServiceBaseJsonLd({
		siteUrl,
		name: 'Webseiten & Hosting',
		description:
			'Professionelle Webseiten mit WordPress oder massgeschneidert mit dem Epic Stack, sicher gehostet auf Schweizer Servern.',
	})
}

/**
 * Service schema for the Cloudron page. No offers: every platform gets an
 * individual quote, so the page publishes no price.
 */
export function generateCloudronServiceJsonLd(siteUrl: string) {
	return generateServiceBaseJsonLd({
		siteUrl,
		name: 'Managed Cloudron Hosting',
		description:
			'Managed Cloudron Hosting auf Schweizer Servern, Ihre eigene Cloud-Plattform mit über 100 Apps, automatischen Updates und täglichen Backups.',
	})
}

/**
 * Service schema for the Managed Nextcloud page. No offers: Organisationen
 * get an individual quote, so the page publishes no price.
 */
export function generateNextcloudServiceJsonLd(siteUrl: string) {
	return generateServiceBaseJsonLd({
		siteUrl,
		name: 'Managed Nextcloud',
		description:
			'Managed Nextcloud Hosting mit Standort Schweiz für Organisationen: tägliche Backups, ein SLA mit fester Reaktionszeit, auf Wunsch Single Sign-on sowie Beratung und Entwicklung aus einer Hand.',
	})
}

/**
 * Service schema for the Coaching page (single hourly rate).
 */
export function generateCoachingServiceJsonLd(siteUrl: string) {
	return generateServiceJsonLd({
		siteUrl,
		name: 'Coaching Digitale Selbstbestimmung',
		description:
			'Persönliches 1:1-Coaching für den Ausstieg aus Big Tech: Linux, sicheres Handy, E-Mail, Cloud. In der ganzen Deutschschweiz.',
		offers: {
			'@type': 'Offer',
			price: '120',
			priceCurrency: 'CHF',
			unitText: 'HUR',
		},
	})
}

/**
 * AboutPage + Organization schema for the About page.
 */
export function generateAboutPageJsonLd(siteUrl: string, lang: string) {
	const base = siteUrl.replace(/\/$/, '')
	const url = canonicalUrl(siteUrl, lang, '/about')
	return {
		'@context': 'https://schema.org',
		'@type': 'AboutPage',
		name: 'Über uns | Open Sourced',
		url,
		mainEntity: {
			...ORGANIZATION_BASE,
			url: base,
			founder: [
				{ '@type': 'Person', name: 'Orell' },
				{ '@type': 'Person', name: 'Fabian' },
			],
			address: {
				'@type': 'PostalAddress',
				addressLocality: 'Lostorf',
				addressCountry: 'CH',
			},
		},
	}
}

/**
 * ContactPoint schema for the Contact page.
 */
export function generateContactPointJsonLd(siteUrl: string, lang: string) {
	const base = siteUrl.replace(/\/$/, '')
	const url = canonicalUrl(siteUrl, lang, '/contact')
	return {
		'@context': 'https://schema.org',
		'@type': 'ContactPage',
		name: 'Kontakt | Open Sourced',
		url,
		mainEntity: {
			'@type': 'ContactPoint',
			email: 'info@opensourced.ch',
			contactType: 'customer service',
			areaServed: 'CH',
			availableLanguage: ['German', 'French', 'English'],
		},
	}
}

/**
 * Generate canonical and hreflang link descriptors for a marketing page.
 */
export function generateCanonicalLinks({
	path,
	lang,
	siteUrl,
}: {
	path: string
	lang: string
	siteUrl: string
}): Array<{ tagName: 'link'; rel: string; href: string; hrefLang?: string }> {
	const base = siteUrl.replace(/\/$/, '')
	const href = canonicalUrl(siteUrl, lang, path)

	const links: Array<{
		tagName: 'link'
		rel: string
		href: string
		hrefLang?: string
	}> = [{ tagName: 'link', rel: 'canonical', href }]

	for (const l of SUPPORTED_LANGUAGES) {
		links.push({
			tagName: 'link',
			rel: 'alternate',
			hrefLang: l,
			href:
				path === '/' || path === ''
					? `${base}/${l}`
					: `${base}${localizedPath(l, path)}`,
		})
	}

	links.push({
		tagName: 'link',
		rel: 'alternate',
		hrefLang: 'x-default',
		href:
			path === '/' || path === ''
				? `${base}/${DEFAULT_LANGUAGE}`
				: `${base}${localizedPath(DEFAULT_LANGUAGE, path)}`,
	})

	return links
}
